import { useEffect, useMemo, useState } from 'react';
import { Edit, FileDown, Loader2, Plus, Save, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdvancedTable } from '../components/AdvancedTable';
import { vehicleAssignmentFormService } from '../services/vehicleAssignmentFormService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { driverService } from '../services/driverService';
import { documentService } from '../services/documentService';
import { staticDefinitionService } from '../services/staticDefinitionService';
import { exportRowsToExcel } from '../utils/excelExport';

interface JsonItem {
  id: number;
  label: string;
  value: string;
  brand?: string;
}

const TIRE_POSITIONS = ['Sol Ön', 'Sağ Ön', 'Sol Arka Dış', 'Sol Arka İç', 'Sağ Arka Dış', 'Sağ Arka İç', 'Stepne'];
const EQUIPMENT_ITEMS = ['Yangın Tüpü', 'İlk Yardım Çantası', 'Reflektör', 'Çekme Halatı', 'Takoz', 'Uyarı Üçgeni', 'Emniyet Yeleği', 'Kriko', 'Bijon Anahtarı', 'Zincir Takımı', 'Kumanda Anahtarları'];
const GADGET_ITEMS = ['Takograf', 'Araç Takip Cihazı', 'Park Sensörü', 'Arka Görüş Kamerası', 'Mobil Terminal', 'El Terminali'];
const DOC_ITEMS = ['Ruhsat', 'Zorunlu Mali Sorumluluk Sigortası', 'Kasko', 'Araç Muayene', 'EGK/Yetki Belgesi', 'Filo Kartı', 'OGS Cihazı'];
const SEAL_TYPES = ['Yakıt Deposu Mührü'];
const TIRE_OPTIONS = ['İyi', 'Kötü', 'Değiştirilmeli', 'Yok'];
const EQUIPMENT_OPTIONS = ['Var', 'Yok', 'Hasarlı'];
const GADGET_OPTIONS = ['Çalışıyor', 'Arızalı', 'Yok'];
const DOCUMENT_OPTIONS = ['Var', 'Yok', 'Süresi Dolmuş'];

const FILE_FIELDS = [
  { key: 'vehicleImageDocumentIds', uploadKey: 'vehicleImages', label: 'Araç Resimleri', documentType: 'vehicle-assignment-vehicle-image' },
  { key: 'tireImageDocumentIds', uploadKey: 'tireImages', label: 'Lastik Resimleri', documentType: 'vehicle-assignment-tire-image' },
  { key: 'driverDocumentIds', uploadKey: 'driverDocuments', label: 'Sürücü Belgeleri', documentType: 'vehicle-assignment-driver-document' },
  { key: 'vehicleVideoDocumentIds', uploadKey: 'vehicleVideos', label: 'Araç Videosu', documentType: 'vehicle-assignment-vehicle-video' },
] as const;

function buildJsonList(labels: string[], existing: JsonItem[]): JsonItem[] {
  if (existing && existing.length > 0) return existing.map((item, index) => ({ id: item.id || index + 1, label: item.label || labels[index] || '', value: item.value || '', brand: item.brand || '' }));
  return labels.map((label, i) => ({ id: i + 1, label, value: '', brand: '' }));
}

function defaultTires(): JsonItem[] {
  return TIRE_POSITIONS.map((label, i) => ({ id: i + 1, label, value: 'İyi', brand: '' }));
}
function defaultEquipment(): JsonItem[] {
  return EQUIPMENT_ITEMS.map((label, i) => ({ id: i + 1, label, value: 'Var' }));
}
function defaultGadgets(): JsonItem[] {
  return GADGET_ITEMS.map((label, i) => ({ id: i + 1, label, value: '' }));
}
function defaultDocs(): JsonItem[] {
  return DOC_ITEMS.map((label, i) => ({ id: i + 1, label, value: '' }));
}
function defaultSeals(): JsonItem[] {
  return SEAL_TYPES.map((label, i) => ({ id: i + 1, label, value: '' }));
}

type AssignmentForm = {
  formNo: string;
  vehicleId: string;
  vehiclePlate: string;
  trailerId: string;
  trailerPlate: string;
  driverId: string;
  driverName: string;
  driverTc: string;
  driverLicenseNo: string;
  driverLicenseClass: string;
  driverPhone: string;
  assignmentDate: string;
  fuelRatio: string;
  notes: string;
  tires: JsonItem[];
  equipment: JsonItem[];
  gadgets: JsonItem[];
  documents: JsonItem[];
  seals: JsonItem[];
  vehicleImageDocumentIds: string[];
  tireImageDocumentIds: string[];
  driverDocumentIds: string[];
  vehicleVideoDocumentIds: string[];
};

const buildInitialForm = (): AssignmentForm => ({
  formNo: String(Date.now()),
  vehicleId: '',
  vehiclePlate: '',
  trailerId: '',
  trailerPlate: '',
  driverId: '',
  driverName: '',
  driverTc: '',
  driverLicenseNo: '',
  driverLicenseClass: '',
  driverPhone: '',
  assignmentDate: new Date().toISOString().slice(0, 10),
  fuelRatio: '',
  notes: '',
  tires: defaultTires(),
  equipment: defaultEquipment(),
  gadgets: defaultGadgets(),
  documents: defaultDocs(),
  seals: defaultSeals(),
  vehicleImageDocumentIds: [],
  tireImageDocumentIds: [],
  driverDocumentIds: [],
  vehicleVideoDocumentIds: [],
});

export function VehicleAssignmentForms() {
  const [rows, setRows] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<Array<{ id: string; label: string; trailerPlate: string; assignedDriverId: string; assignedDriverName: string; fuelRatio: string }>>([]);
  const [trailers, setTrailers] = useState<Array<{ id: string; label: string }>>([]);
  const [drivers, setDrivers] = useState<Array<{ id: string; label: string; tc: string; licenseNo: string; licenseClass: string; phone: string }>>([]);
  const [tireBrandOptions, setTireBrandOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [form, setForm] = useState<AssignmentForm>(buildInitialForm());
  const [uploadFiles, setUploadFiles] = useState<Record<string, File[]>>({});

  const loadData = async () => {
    try {
      setLoading(true);
      const [formRes, vehicleRes, trailerRes, driverRes, tireBrandRes] = await Promise.all([
        vehicleAssignmentFormService.getAll(),
        vehicleService.getVehicles(),
        trailerService.getTrailers(),
        driverService.getDrivers({ type: 'internal' }),
        staticDefinitionService.getByKey('vehicle_assignment_tire_brands').catch(() => ({ data: null })),
      ]);

      const rawVehicles = Array.isArray((vehicleRes as any)?.data) ? (vehicleRes as any).data : [];
      setVehicles(rawVehicles.map((item: any) => ({
        id: String(item.id),
        label: item.cekici_plaka || item.plate || '-',
        trailerPlate: item.linked_trailer_plate || item.dorse_plaka || '',
        assignedDriverId: item.assigned_driver_id ? String(item.assigned_driver_id) : '',
        assignedDriverName: item.driver_name || '',
        fuelRatio: item.fuel_level != null ? String(Number(item.fuel_level).toFixed(2)) : '',
      })));

      const rawTrailers = Array.isArray((trailerRes as any)?.data) ? (trailerRes as any).data : [];
      setTrailers(rawTrailers.map((item: any) => ({ id: String(item.id), label: item.plate || '-' })));

      const rawDrivers = Array.isArray((driverRes as any)?.data) ? (driverRes as any).data : [];
      setDrivers(rawDrivers.map((item: any) => ({
        id: String(item.id),
        label: item.full_name || item.name || '-',
        tc: item.tc || item.tc_no || '',
        licenseNo: item.license_no || item.ehliyet_no || '',
        licenseClass: item.license_class || item.ehliyet_sinifi || '',
        phone: item.phone || item.telefon || '',
      })));

      const tireBrandItems = Array.isArray((tireBrandRes as any)?.data?.items) ? (tireBrandRes as any).data.items : [];
      setTireBrandOptions(tireBrandItems.map((item: any) => String(item.value || '').trim()).filter(Boolean));

      setRows(Array.isArray((formRes as any)?.data) ? (formRes as any).data : []);
    } catch (error: any) {
      console.error('Vehicle assignment data load failed:', error);
      toast.error(error?.message || 'Araç zimmet formu verileri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const openCreate = () => {
    setEditingRow(null);
    setUploadFiles({});
    setForm(buildInitialForm());
    setModalOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingRow(row);
    setUploadFiles({});
    setForm({
      ...buildInitialForm(),
      formNo: row.formNo || String(Date.now()),
      vehicleId: String(row.vehicleId || ''),
      vehiclePlate: row.vehiclePlate || '',
      trailerId: String(row.trailerId || ''),
      trailerPlate: row.trailerPlate || '',
      driverId: String(row.driverId || ''),
      driverName: row.driverName || '',
      driverTc: row.driverTc || '',
      driverLicenseNo: row.driverLicenseNo || '',
      driverLicenseClass: row.driverLicenseClass || '',
      driverPhone: row.driverPhone || '',
      assignmentDate: row.assignmentDate ? String(row.assignmentDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
      fuelRatio: row.fuelRatio != null ? String(row.fuelRatio) : '',
      notes: row.notes || '',
      tires: buildJsonList(TIRE_POSITIONS, row.tires),
      equipment: buildJsonList(EQUIPMENT_ITEMS, row.equipment),
      gadgets: buildJsonList(GADGET_ITEMS, row.gadgets),
      documents: buildJsonList(DOC_ITEMS, row.documents),
      seals: buildJsonList(SEAL_TYPES, row.seals),
      vehicleImageDocumentIds: Array.isArray(row.vehicleImageDocumentIds) ? row.vehicleImageDocumentIds.map(String) : [],
      tireImageDocumentIds: Array.isArray(row.tireImageDocumentIds) ? row.tireImageDocumentIds.map(String) : [],
      driverDocumentIds: Array.isArray(row.driverDocumentIds) ? row.driverDocumentIds.map(String) : [],
      vehicleVideoDocumentIds: Array.isArray(row.vehicleVideoDocumentIds) ? row.vehicleVideoDocumentIds.map(String) : [],
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setForm(buildInitialForm());
    setUploadFiles({});
  };

  const handleSelectLinkedOption = (field: 'vehicle' | 'trailer' | 'driver', value: string) => {
    if (field === 'vehicle') {
      const selected = vehicles.find((item) => item.label === value);
      const matchedTrailer = trailers.find((item) => item.label === (selected?.trailerPlate || ''));
      const matchedDriver = drivers.find((item) => item.id === selected?.assignedDriverId);
      setForm((current) => ({
        ...current,
        vehicleId: selected?.id || '',
        vehiclePlate: value,
        trailerId: matchedTrailer?.id || current.trailerId,
        trailerPlate: selected?.trailerPlate || current.trailerPlate,
        driverId: matchedDriver?.id || current.driverId,
        driverName: matchedDriver?.label || selected?.assignedDriverName || current.driverName,
        driverTc: matchedDriver?.tc || current.driverTc,
        driverLicenseNo: matchedDriver?.licenseNo || current.driverLicenseNo,
        driverLicenseClass: matchedDriver?.licenseClass || current.driverLicenseClass,
        driverPhone: matchedDriver?.phone || current.driverPhone,
        fuelRatio: selected?.fuelRatio || current.fuelRatio,
      }));
      return;
    }
    if (field === 'trailer') {
      const selected = trailers.find((item) => item.label === value);
      setForm((current) => ({ ...current, trailerId: selected?.id || '', trailerPlate: value }));
      return;
    }
    const selected = drivers.find((item) => item.label === value);
    setForm((current) => ({
      ...current,
      driverId: selected?.id || '',
      driverName: value,
      driverTc: selected?.tc || current.driverTc,
      driverLicenseNo: selected?.licenseNo || current.driverLicenseNo,
      driverLicenseClass: selected?.licenseClass || current.driverLicenseClass,
      driverPhone: selected?.phone || current.driverPhone,
    }));
  };

  const uploadSelectedFiles = async (recordId: string, key: string, documentType: string) => {
    const files = uploadFiles[key] || [];
    if (files.length === 0) return [] as string[];
    const ids: string[] = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'vehicle_assignment');
      formData.append('entityId', recordId);
      formData.append('documentType', documentType);
      formData.append('name', file.name);
      const response: any = await documentService.uploadDocument(formData);
      if (response?.data?.id) ids.push(String(response.data.id));
    }
    return ids;
  };

  const buildRequestPayload = (nextForm: AssignmentForm) => ({
    formNo: nextForm.formNo,
    vehicleId: nextForm.vehicleId || null,
    vehiclePlate: nextForm.vehiclePlate || null,
    trailerId: nextForm.trailerId || null,
    trailerPlate: nextForm.trailerPlate || null,
    driverId: nextForm.driverId || null,
    driverName: nextForm.driverName || null,
    driverTc: nextForm.driverTc || null,
    driverLicenseNo: nextForm.driverLicenseNo || null,
    driverLicenseClass: nextForm.driverLicenseClass || null,
    driverPhone: nextForm.driverPhone || null,
    assignmentDate: nextForm.assignmentDate || null,
    fuelRatio: nextForm.fuelRatio !== '' ? Number(nextForm.fuelRatio) : null,
    notes: nextForm.notes || null,
    tires: nextForm.tires,
    equipment: nextForm.equipment,
    gadgets: nextForm.gadgets,
    documents: nextForm.documents,
    seals: nextForm.seals,
    vehicleImageDocumentIds: nextForm.vehicleImageDocumentIds,
    tireImageDocumentIds: nextForm.tireImageDocumentIds,
    driverDocumentIds: nextForm.driverDocumentIds,
    vehicleVideoDocumentIds: nextForm.vehicleVideoDocumentIds,
  });

  const handleSave = async () => {
    if (!form.vehiclePlate) { toast.error('Araç seçimi zorunludur'); return; }
    if (!form.driverName) { toast.error('Sürücü seçimi zorunludur'); return; }

    try {
      setSaving(true);
      const basePayload = buildRequestPayload(form);
      if (editingRow) {
        const updateRes: any = await vehicleAssignmentFormService.update(editingRow.id, basePayload);
        if (!updateRes?.success) throw new Error(updateRes?.message || 'Kayıt güncellenemedi');

        const nextForm = { ...form };
        for (const field of FILE_FIELDS) {
          const ids = await uploadSelectedFiles(editingRow.id, field.uploadKey, field.documentType);
          if (ids.length > 0) {
            nextForm[field.key] = [...new Set([...(nextForm[field.key] || []), ...ids])] as string[];
          }
        }

        if (JSON.stringify(buildRequestPayload(nextForm)) !== JSON.stringify(basePayload)) {
          await vehicleAssignmentFormService.update(editingRow.id, buildRequestPayload(nextForm));
        }
      } else {
        const createRes: any = await vehicleAssignmentFormService.create(basePayload);
        if (!createRes?.success || !createRes?.data?.id) throw new Error(createRes?.message || 'Kayıt oluşturulamadı');
        const recordId = String(createRes.data.id);

        const nextForm = { ...form };
        for (const field of FILE_FIELDS) {
          const ids = await uploadSelectedFiles(recordId, field.uploadKey, field.documentType);
          if (ids.length > 0) {
            (nextForm as any)[field.key] = ids;
          }
        }

        if (JSON.stringify(buildRequestPayload(nextForm)) !== JSON.stringify(basePayload)) {
          await vehicleAssignmentFormService.update(recordId, buildRequestPayload(nextForm));
        }
      }

      toast.success(editingRow ? 'Araç zimmet formu güncellendi' : 'Araç zimmet formu oluşturuldu');
      closeModal();
      await loadData();
    } catch (error: any) {
      console.error('Vehicle assignment save failed:', error);
      toast.error(error?.message || 'Araç zimmet formu kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm('Bu zimmet formunu silmek istediğinize emin misiniz?')) return;
    try {
      const response: any = await vehicleAssignmentFormService.delete(row.id);
      if (!response?.success) throw new Error(response?.message || 'Kayıt silinemedi');
      toast.success('Araç zimmet formu silindi');
      await loadData();
    } catch (error: any) {
      console.error('Vehicle assignment delete failed:', error);
      toast.error(error?.message || 'Araç zimmet formu silinemedi');
    }
  };

  const handlePrintPdf = async (row: any) => {
    try {
      const blob = await vehicleAssignmentFormService.downloadPrintPdf(row.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vehicle-assignment-form-${row.formNo || row.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF oluşturuldu');
    } catch (error: any) {
      console.error('Vehicle assignment pdf download failed:', error);
      toast.error(error?.message || 'PDF oluşturulamadı');
    }
  };

  const renderJsonPreview = (items: JsonItem[]): string => {
    if (!items || items.length === 0) return '-';
    const total = items.length;
    const filled = items.filter(i => i.value && i.value.trim() !== '' && i.value !== 'Yok').length;
    return `${filled}/${total}`;
  };

  const tableData = useMemo(() => rows.map((row) => ({
    id: row.id,
    formNo: row.formNo || row.id,
    vehiclePlate: row.vehiclePlate || '-',
    trailerPlate: row.trailerPlate || '-',
    driverName: row.driverName || '-',
    driverTc: row.driverTc || '-',
    driverLicenseClass: row.driverLicenseClass || '-',
    driverPhone: row.driverPhone || '-',
    assignmentDate: row.assignmentDate ? new Date(row.assignmentDate).toLocaleDateString('tr-TR') : '-',
    fuelRatio: row.fuelRatio != null ? `%${Number(row.fuelRatio).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
    tireSummary: renderJsonPreview(row.tires),
    equipmentSummary: renderJsonPreview(row.equipment),
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString('tr-TR') : '-',
  })), [rows]);

  const tableColumns = useMemo(() => [
    { key: 'formNo', label: 'Form No' },
    { key: 'vehiclePlate', label: 'Plaka' },
    { key: 'trailerPlate', label: 'Dorse' },
    { key: 'driverName', label: 'Sürücü' },
    { key: 'driverTc', label: 'TC No' },
    { key: 'driverLicenseClass', label: 'Ehliyet Sınıfı' },
    { key: 'driverPhone', label: 'Telefon' },
    { key: 'assignmentDate', label: 'Zimmet Tarihi' },
    { key: 'fuelRatio', label: 'Yakıt Oranı' },
    { key: 'tireSummary', label: 'Lastik' },
    { key: 'equipmentSummary', label: 'Avadanlık' },
    { key: 'createdAt', label: 'Kayıt Tarihi' },
    {
      key: 'id',
      label: 'İşlem',
      render: (_value: string, record: any) => {
        const rawRow = rows.find((item) => String(item.id) === String(record.id));
        if (!rawRow) return null;
        return (
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(rawRow); }} className="rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100">
              <Edit className="mr-1 inline h-4 w-4" /> Düzenle
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); void handlePrintPdf(rawRow); }} className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100">
              <FileDown className="mr-1 inline h-4 w-4" /> Yazdır
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); void handleDelete(rawRow); }} className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100">
              <Trash2 className="mr-1 inline h-4 w-4" /> Sil
            </button>
          </div>
        );
      },
    },
  ], [rows]);

  const renderLinkedField = (label: string, field: 'vehicle' | 'trailer' | 'driver', value: string, options: Array<{ id: string; label: string }>, placeholder: string) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <div>
        <input
          list={`vehicle-assignment-${field}-options`}
          value={value}
          onChange={(e) => handleSelectLinkedOption(field, e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
        />
        <datalist id={`vehicle-assignment-${field}-options`}>
          {options.map((item) => <option key={item.id} value={item.label}>{item.label}</option>)}
        </datalist>
      </div>
    </div>
  );

  const renderTextareaField = (label: string, key: string) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <textarea
        value={(form as any)[key] || ''}
        onChange={(e) => setForm((current) => ({ ...current, [key]: e.target.value }))}
        placeholder="Metin Ekle"
        rows={2}
        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
      />
    </div>
  );

  const renderSegmentField = (label: string, value: string, options: string[], onChange: (value: string) => void) => (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${value === option ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Araç Zimmet Formları</h1>
          <p className="mt-1 text-gray-500">Araç zimmet formlarını doldurun, kaydedin ve yönetin.</p>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => exportRowsToExcel(tableData as any[], `vehicle-assignment-forms-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'VehicleAssignmentForms' })} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Excele Aktar
          </button>
          <button type="button" onClick={openCreate} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <Plus className="mr-2 inline h-4 w-4" /> Yeni Form
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <AdvancedTable
          data={tableData as any}
          columns={tableColumns as any}
          onRefresh={() => void loadData()}
          onRowDoubleClick={(record: any) => {
            const row = rows.find((item) => String(item.id) === String(record.id));
            if (row) openEdit(row);
          }}
        />
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">{editingRow ? 'Araç Zimmet Formu Düzenle' : 'Yeni Araç Zimmet Formu'}</h2>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-5 p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Form No</label>
                  <input type="text" value={form.formNo} onChange={(e) => setForm((current) => ({ ...current, formNo: e.target.value }))} placeholder="Form No" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                {renderLinkedField('Plaka', 'vehicle', form.vehiclePlate, vehicles, 'Bağlantılı liste kaydı bul ve ekle')}
                {renderLinkedField('Dorse', 'trailer', form.trailerPlate, trailers, 'Bağlantılı liste kaydı bul ve ekle')}
                {renderLinkedField('Sürücü', 'driver', form.driverName, drivers, 'Bağlantılı liste kaydı bul ve ekle')}

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">TC No</label>
                  <input type="text" value={form.driverTc} onChange={(e) => setForm((current) => ({ ...current, driverTc: e.target.value }))} placeholder="TC Kimlik No" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Ehliyet No</label>
                  <input type="text" value={form.driverLicenseNo} onChange={(e) => setForm((current) => ({ ...current, driverLicenseNo: e.target.value }))} placeholder="Ehliyet No" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Ehliyet Sınıfı</label>
                  <input type="text" value={form.driverLicenseClass} onChange={(e) => setForm((current) => ({ ...current, driverLicenseClass: e.target.value }))} placeholder="Ehliyet Sınıfı" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Telefon</label>
                  <input type="text" value={form.driverPhone} onChange={(e) => setForm((current) => ({ ...current, driverPhone: e.target.value }))} placeholder="Telefon" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Zimmet Tarihi</label>
                  <input type="date" value={form.assignmentDate} onChange={(e) => setForm((current) => ({ ...current, assignmentDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                  <label className="text-sm font-semibold text-slate-700">Yakıt Oranı</label>
                  <div className="flex gap-3">
                    <input type="text" value={form.fuelRatio} onChange={(e) => setForm((current) => ({ ...current, fuelRatio: e.target.value }))} placeholder="Seyirden otomatik gelir" className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm" />
                    <div className="flex items-center rounded-lg bg-slate-100 px-4 text-sm font-medium text-slate-600">%</div>
                  </div>
                </div>

                {form.tires.map((item) => (
                  <div key={item.id} className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-start">
                    <label className="text-sm font-semibold text-slate-700">Lastik - {item.label}</label>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {TIRE_OPTIONS.map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setForm((current) => ({ ...current, tires: current.tires.map((t) => t.id === item.id ? { ...t, value: option } : t) }))}
                            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${item.value === option ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      <select
                        value={item.brand || ''}
                        onChange={(e) => setForm((current) => ({ ...current, tires: current.tires.map((t) => t.id === item.id ? { ...t, brand: e.target.value } : t) }))}
                        className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                      >
                        <option value="">Lastik Markası Seçiniz</option>
                        {tireBrandOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </div>
                  </div>
                ))}

                {form.equipment.map((item) => (
                  <div key={item.id}>
                    {renderSegmentField(`Avadanlık - ${item.label}`, item.value, EQUIPMENT_OPTIONS, (value) => setForm((current) => ({ ...current, equipment: current.equipment.map((t) => t.id === item.id ? { ...t, value } : t) })))}
                  </div>
                ))}

                {form.gadgets.map((item) => (
                  <div key={item.id}>
                    {renderSegmentField(`Teçhizat - ${item.label}`, item.value, GADGET_OPTIONS, (value) => setForm((current) => ({ ...current, gadgets: current.gadgets.map((t) => t.id === item.id ? { ...t, value } : t) })))}
                  </div>
                ))}

                {form.documents.map((item) => (
                  <div key={item.id}>
                    {renderSegmentField(`Belge - ${item.label}`, item.value, DOCUMENT_OPTIONS, (value) => setForm((current) => ({ ...current, documents: current.documents.map((t) => t.id === item.id ? { ...t, value } : t) })))}
                  </div>
                ))}

                {form.seals.map((item) => (
                  <div key={item.id} className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                    <label className="text-sm font-semibold text-slate-700">Mühür - {item.label}</label>
                    <input
                      value={item.value}
                      onChange={(e) => setForm((current) => ({ ...current, seals: current.seals.map((t) => t.id === item.id ? { ...t, value: e.target.value } : t) }))}
                      placeholder="Mühür no"
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm"
                    />
                  </div>
                ))}

                {FILE_FIELDS.map((field) => (
                  <div key={field.key} className="grid grid-cols-1 gap-2 md:grid-cols-[240px_minmax(0,1fr)] md:items-center">
                    <label className="text-sm font-semibold text-slate-700">{field.label}</label>
                    <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-300 px-4 py-3 text-sm text-slate-500 hover:bg-gray-50">
                      <span>Alan seçiniz ve alanları yapıştırınız veya sürükleyiniz</span>
                      <span className="font-medium text-blue-600"><Upload className="mr-1 inline h-4 w-4" /> Yükle</span>
                      <input type="file" multiple accept={field.uploadKey === 'vehicleVideos' ? 'video/*' : 'image/*'} className="hidden" onChange={(e) => setUploadFiles((current) => ({ ...current, [field.uploadKey]: Array.from(e.target.files || []) }))} />
                    </label>
                    <div className="md:col-start-2 text-xs text-gray-500">
                      Mevcut: {((form as any)[field.key] || []).length} dosya{(uploadFiles[field.uploadKey] || []).length > 0 ? ` • Yeni: ${(uploadFiles[field.uploadKey] || []).map((file) => file.name).join(', ')}` : ''}
                    </div>
                  </div>
                ))}

                {renderTextareaField('Notlar', 'notes')}
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={() => void handleSave()} disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> : <Save className="mr-2 inline h-4 w-4" />} Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
